import { ConfigRegistryType, ConfigRegistryConfig, ConfigRenderType } from 'types/apis/pilotInterfaces';

const normalizeRenderType = (renderType: any): string => String(renderType || '').toUpperCase();

/** Render types whose value is held as string[] in form state, Redux and save payloads */
export function isMultiSelectRenderType(renderType: any): boolean {
  const rt = normalizeRenderType(renderType);
  return rt === 'MULTI_SELECT_ARRAY' || rt === 'MULTI_SELECT_COMMA_SEPERATED_TEXT_ARRAY';
}

export function isSelectRenderType(renderType: any): boolean {
  const rt = normalizeRenderType(renderType);
  return rt === 'SINGLE_SELECT_ARRAY' || isMultiSelectRenderType(rt);
}

export function isBooleanRenderType(renderType: any): boolean {
  return normalizeRenderType(renderType) === 'BOOLEAN';
}

/**
 * Allowed component names to process from registry
 */
export const ALLOWED_COMPONENTS: ReadonlySet<string> = new Set([
  'Aggregation',
  'Disaggregation',
  'Ingestion',
]);

/**
 * Filter registry entries to only include configs whose component is in the allowed list
 * Handles component as a single value or comma-separated items (e.g., "Aggregation,Disaggregation" or "Aggregation")
 */
export function filterRegistryByComponents(
  registry: ConfigRegistryType[],
  allowedComponents: ReadonlySet<string> = ALLOWED_COMPONENTS
): ConfigRegistryType[] {
  console.log('filterRegistryByComponents', registry);
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => {
        const component = (cfg as any).component;
        if (!component) {
          return false;
        }
        
        // If component is a string, split by comma and check if any match
        if (typeof component === 'string') {
          const components = component
            .split(',')
            .map((c: string) => c.trim())
            .filter(Boolean);
          
          // Check if any of the split components are in the allowed set
          return components.some((comp: string) => allowedComponents.has(comp));
        }
        
        // Fallback: check if the component value itself is in the allowed set
        return allowedComponents.has(String(component));
      })
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Serialize a form value for saving, per the config's renderType.
 * MULTI_SELECT_ARRAY saves a JSON array string; every other type saves a plain/comma string.
 */
export function processConfigValue(value: any, renderType: ConfigRenderType | string | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // No renderType means the registry hasn't declared how to reshape this value
  // (e.g. array-string defaults like "[]") — save it exactly as given.
  if (renderType === null || renderType === undefined) {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  if (normalizeRenderType(renderType) === 'MULTI_SELECT_ARRAY') {
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    // Already-serialized JSON keeps its shape
    if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
      return value;
    }
    if (typeof value === 'string') {
      return JSON.stringify(value.split(',').map(item => item.trim()));
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return JSON.stringify([value]);
  }

  // TEXT, BOOLEAN, SINGLE_SELECT_ARRAY, MULTI_SELECT_COMMA_SEPERATED_TEXT_ARRAY
  if (Array.isArray(value)) {
    return value.join(',');
  }
  if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.join(',');
      }
    } catch {
      // Not valid JSON — fall through and save as-is
    }
  }
  if (typeof value === 'string') {
    return value;
  }
  // Map-shaped selections save as a JSON object
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Returns true if the given config entry is marked as a duplicate (per new or legacy schema).
 */
export function isDuplicateConfig(config: ConfigRegistryConfig | any): boolean {
  const dup = (config as any)?.duplicateConfig;
  
  // New pattern: array of objects with configKey and configType
  if (Array.isArray(dup) && dup.length > 0) {
    // Check if it's the new pattern (array of objects with configKey and configType)
    if (dup[0] && typeof dup[0] === 'object' && 'configKey' in dup[0] && 'configType' in dup[0]) {
      return dup.length > 0;
    }
    // Legacy pattern: array of strings
    return dup.length > 0;
  }
  return false;
}

/**
 * Returns a deep-copied registry where any configs marked as duplicates are removed from each type.
 */
export function stripDuplicateConfigs(registry: ConfigRegistryType[]): ConfigRegistryType[] {
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => !isDuplicateConfig(cfg))
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Helper to apply an additional predicate while also stripping duplicates.
 */
export function filterConfigsNoDuplicates(
  registry: ConfigRegistryType[],
  predicate: (cfg: any) => boolean
): ConfigRegistryType[] {
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => predicate(cfg) && !isDuplicateConfig(cfg))
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Interface for config group selection state
 */
export interface ConfigGroupSelection {
  [groupKey: string]: string; // normalized groupKey -> selected category
}

/**
 * The one config group, hardcoded in the FE: the user picks a batch data source by radio,
 * and only configs tagged with that `dataSource` are rendered and saved.
 * The key is also the `unique_groups` key persisted on the pilot — see PilotDetailsPage.
 */
export const DATA_SOURCE_GROUP_KEY = 'S3_Pilot|SFTP_Pilot';
export const DATA_SOURCE_OPTIONS = ['S3_Pilot', 'SFTP_Pilot'] as const;

/**
 * True when a config should be included for the current data-source choice:
 * configs with no `dataSource` are not source-specific and always apply; the rest only
 * apply to the selected source (so nothing source-specific is included until a choice is made).
 */
export function matchesDataSourceSelection(config: any, groupSelection: ConfigGroupSelection): boolean {
  const dataSource: string | null | undefined = config?.dataSource;
  if (!dataSource) return true;
  return dataSource === groupSelection[DATA_SOURCE_GROUP_KEY];
}

/**
 * Filter configs by the selected data source
 */
export function filterConfigsByGroupSelection(
  registry: ConfigRegistryType[],
  groupSelection: ConfigGroupSelection
): ConfigRegistryType[] {
  return registry.map(configType => ({
    ...configType,
    configs: configType.configs.filter(config => matchesDataSourceSelection(config, groupSelection))
  }));
}

/**
 * Display order for the dynamic config form, hardcoded now that the registry no longer sends ConfigOrder.
 *
 * Only non-dependent configs need listing: DynamicConfigForm's dependency pass already renders a
 * dependent config immediately after its parent, so per-fuel unit dropdowns follow their fuel field
 * without an entry here. Any configKey not listed keeps registry order, after the listed ones.
 */
export const CONFIG_DISPLAY_ORDER: string[] = [
  'billingCycleIngesterType',
  'decryption_passphrase',
  // Fuel type sits directly above the per-fuel maps that are scoped by it
  'allowed_measurement_type',
  'raw_data_consumption_unit_map',
  'invoice_data_consumption_unit_map',
  'isEndDateInclusive',
  'gas_unit',
  'water_unit',
];

const FUEL_MAP_KEYS = ['ELECTRIC', 'GAS', 'WATER'];

/** The fuel-type field, used as the implicit parent of fuel-keyed maps */
export const FUEL_TYPE_DEP_REF = { configKey: 'allowed_measurement_type' };

/**
 * True when map-shaped possibleValues are keyed by fuel (ELECTRIC/GAS/WATER).
 * Such maps are scoped to the fuels picked on step 1 even when the registry sends no
 * configDependsUpon — without this they would render a section for every fuel.
 */
export function isFuelKeyedMap(mapKeys: string[]): boolean {
  return (
    mapKeys.length > 0 && mapKeys.every(key => FUEL_MAP_KEYS.includes(String(key).toUpperCase()))
  );
}

/**
 * Position of a configKey in CONFIG_DISPLAY_ORDER; unlisted keys sort last.
 * Applied across the whole flattened form, not per configType — the order is one global sequence.
 */
export function configDisplayRank(configKey: string): number {
  const index = CONFIG_DISPLAY_ORDER.indexOf(configKey);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/**
 * Get configs that should be saved for the selected data source
 */
export function getConfigsToSave(
  registry: ConfigRegistryType[],
  groupSelection: ConfigGroupSelection
): ConfigRegistryType[] {
  return filterConfigsByGroupSelection(registry, groupSelection)
    .filter(configType => configType.configs.length > 0);
} 